/**
 * Quick Tour step definitions.
 *
 * Each step targets a DOM element via `data-tour="<targetId>"`.
 * Steps are grouped by page so the tour engine knows when to
 * pause and wait for navigation.
 */

export interface TourStep {
  id: string;
  /** CSS selector or data-tour value used to find the target element */
  targetSelector: string;
  title: string;
  text: string;
  /** Which page this step lives on */
  page: 'dashboard' | 'new-shoot' | 'shoot-detail';
  /** Preferred tooltip placement */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** Custom label for the primary CTA button (defaults to "Next") */
  ctaLabel?: string;
  /** If true, clicking the CTA will programmatically click the target element instead of advancing */
  clickTarget?: boolean;
  /** If true, hide the primary CTA entirely (user must interact with the highlighted element) */
  hideCta?: boolean;
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: 'new-project',
    targetSelector: '[data-tour="new-project"]',
    title: 'tour.new_project.title',
    text: 'tour.new_project.text',
    page: 'dashboard',
    placement: 'bottom',
    ctaLabel: 'tour.new_project.cta',
    clickTarget: true,
  },
  // Tour pauses while navigating to /shoots/new
  {
    id: 'new-shoot-required',
    targetSelector: '[data-tour="new-shoot-required"]',
    title: 'tour.new_shoot_required.title',
    text: 'tour.new_shoot_required.text',
    page: 'new-shoot',
    placement: 'right',
  },
  {
    id: 'new-shoot-optional',
    targetSelector: '[data-tour="new-shoot-optional"]',
    title: 'tour.new_shoot_optional.title',
    text: 'tour.new_shoot_optional.text',
    page: 'new-shoot',
    placement: 'right',
  },
  {
    id: 'new-shoot-submit',
    targetSelector: '[data-tour="new-shoot-submit"]',
    title: 'tour.new_shoot_submit.title',
    text: 'tour.new_shoot_submit.text',
    page: 'new-shoot',
    placement: 'top',
    ctaLabel: 'tour.new_shoot_submit.cta',
    clickTarget: true,
  },
  // After creation, the success screen appears
  {
    id: 'view-shoot-details',
    targetSelector: '[data-tour="view-shoot-details"]',
    title: 'tour.view_shoot_details.title',
    text: 'tour.view_shoot_details.text',
    page: 'new-shoot',
    placement: 'top',
    ctaLabel: 'tour.view_shoot_details.cta',
    clickTarget: true,
  },
  // It resumes automatically when shoot-detail page mounts.
  {
    id: 'status-badge',
    targetSelector: '[data-tour="status-badge"]',
    title: 'tour.status_badge.title',
    text: 'tour.status_badge.text',
    page: 'shoot-detail',
    placement: 'bottom',
  },
  {
    id: 'client-tab',
    targetSelector: '[data-tour="client-tab"]',
    title: 'tour.client_tab.title',
    text: 'tour.client_tab.text',
    page: 'shoot-detail',
    placement: 'bottom',
  },
  {
    id: 'template-area',
    targetSelector: '[data-tour="template-area"]',
    title: 'tour.template_area.title',
    text: 'tour.template_area.text',
    page: 'shoot-detail',
    placement: 'bottom',
  },
  {
    id: 'send-full-brief',
    targetSelector: '[data-tour="send-full-brief"]',
    title: 'tour.send_full_brief.title',
    text: 'tour.send_full_brief.text',
    page: 'shoot-detail',
    placement: 'bottom',
  },
  {
    id: 'send-rating',
    targetSelector: '[data-tour="send-rating"]',
    title: 'tour.send_rating.title',
    text: 'tour.send_rating.text',
    page: 'shoot-detail',
    placement: 'bottom',
  },
  {
    id: 'fill-yourself',
    targetSelector: '[data-tour="fill-yourself"]',
    title: 'tour.fill_yourself.title',
    text: 'tour.fill_yourself.text',
    page: 'shoot-detail',
    placement: 'bottom',
  },
  {
    id: 'share-actions',
    targetSelector: '[data-tour="share-actions"]',
    title: 'tour.share_actions.title',
    text: 'tour.share_actions.text',
    page: 'shoot-detail',
    placement: 'bottom',
  },
  {
    id: 'field-brief-tab',
    targetSelector: '[data-tour="field-brief-tab"]',
    title: 'tour.field_brief_tab.title',
    text: 'tour.field_brief_tab.text',
    page: 'shoot-detail',
    placement: 'bottom',
  },
];

/** Find the first step index for a given page */
export function firstStepIndexForPage(page: string): number {
  return TOUR_STEPS.findIndex(s => s.page === page);
}
